import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Download, Clock, ShieldCheck, AlertTriangle, Music as MusicIcon } from "lucide-react";
import { api, formatApiError, imgUrl, API as API_BASE } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

function formatRelative(iso) {
  if (!iso) return "";
  const target = new Date(iso).getTime();
  const diff = target - Date.now();
  if (diff <= 0) return "expired";
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  if (days >= 1) return `${days} day${days === 1 ? "" : "s"} ${hours}h`;
  return `${hours}h ${Math.floor((diff % 3_600_000) / 60_000)}m`;
}

export default function DownloadLocker() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/public/download/${token}/meta`);
      setMeta(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Couldn't load this download.");
    }
  };

  useEffect(() => {
    const id = setTimeout(refresh, 0);
    return () => clearTimeout(id);
  }, [token]);

  const startDownload = async () => {
    setBusy(true);
    try {
      // Use fetch (with credentials) to handle the binary stream — then trigger a save
      const resp = await fetch(`${API_BASE}/public/download/${token}`);
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      // Pull filename from Content-Disposition header
      const cd = resp.headers.get("content-disposition") || "";
      const match = cd.match(/filename="([^"]+)"/i);
      const filename = match
        ? match[1]
        : `${meta?.release?.slug || "track"}.mp3`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Give the browser a beat before revoking
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      await refresh();
    } catch (err) {
      setError(err.message || "Download failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="py-24 lg:py-32" data-testid="download-locker-page">
      <section className="max-w-[900px] mx-auto px-6 lg:px-12">
        <SectionLabel label="Your Download Locker" />
        <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3 mb-10">
          {meta?.release ? (
            <>
              {meta.release.title}
              <span className="text-2xl text-white/45 ml-3 font-light">
                — {meta.release.artist}
              </span>
            </>
          ) : (
            "Your Download"
          )}
        </h1>

        {error ? (
          <div className="surface p-7 flex items-start gap-3" data-testid="download-error">
            <AlertTriangle size={20} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
            <div>
              <p className="text-white/85 font-medium">{error}</p>
              <p className="text-sm text-white/55 mt-2">
                Lost the link or hit a snag? Email{" "}
                <a href="mailto:bookings@ninacapone.com" className="text-[color:var(--gold)] hover:underline">
                  bookings@ninacapone.com
                </a>{" "}
                with your Stripe receipt and we&rsquo;ll resend.
              </p>
            </div>
          </div>
        ) : !meta ? (
          <p className="text-white/55 text-sm uppercase tracking-[0.22em]">Loading…</p>
        ) : meta.expired ? (
          <div className="surface p-7 flex items-start gap-3" data-testid="download-expired">
            <Clock size={20} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
            <div>
              <p className="text-white/85 font-medium">This download link has expired.</p>
              <p className="text-sm text-white/55 mt-2">
                Links are valid for 7 days after purchase. Email bookings@ninacapone.com
                with your receipt and we&rsquo;ll generate a fresh link.
              </p>
            </div>
          </div>
        ) : meta.exhausted ? (
          <div className="surface p-7 flex items-start gap-3" data-testid="download-exhausted">
            <ShieldCheck size={20} className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }} />
            <div>
              <p className="text-white/85 font-medium">
                You&rsquo;ve used all {meta.max_downloads} downloads for this purchase.
              </p>
              <p className="text-sm text-white/55 mt-2">
                Need another? Email bookings@ninacapone.com.
              </p>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-8" data-testid="download-ready">
            <div className="lg:col-span-5">
              {meta.release?.cover_image ? (
                <img
                  src={imgUrl(meta.release.cover_image)}
                  alt={meta.release.title}
                  className="w-full aspect-square object-cover border border-white/10"
                  data-testid="download-cover"
                />
              ) : (
                <div
                  className="w-full aspect-square flex items-center justify-center border border-white/10"
                  style={{
                    background:
                      "radial-gradient(ellipse 80% 80% at 30% 30%, rgba(232,180,184,0.18), transparent 70%), #1a0d12",
                  }}
                  data-testid="download-cover-fallback"
                >
                  <MusicIcon size={64} style={{ color: "var(--gold)" }} strokeWidth={1} />
                </div>
              )}
            </div>
            <div className="lg:col-span-7 space-y-6">
              {meta.release?.description && (
                <p className="text-white/70 leading-relaxed">{meta.release.description}</p>
              )}
              <button
                type="button"
                onClick={startDownload}
                disabled={busy}
                className="btn-gold inline-flex items-center gap-2 px-8 py-4 text-xs disabled:opacity-40"
                data-testid="download-start-btn"
              >
                {busy ? "Preparing…" : `Download · ${meta.release?.title || "MP3"}`}
                <Download size={14} />
              </button>
              <div className="border-t border-white/5 pt-6 grid sm:grid-cols-2 gap-4 text-[11px] uppercase tracking-[0.22em]">
                <div className="text-white/55">
                  <Clock size={11} className="inline mr-1.5 -mt-0.5" />
                  Expires in{" "}
                  <strong className="text-white" data-testid="download-expires">
                    {formatRelative(meta.expires_at)}
                  </strong>
                </div>
                <div className="text-white/55">
                  <ShieldCheck size={11} className="inline mr-1.5 -mt-0.5" />
                  <strong className="text-white" data-testid="download-counter">
                    {meta.download_count}
                  </strong>{" "}
                  / {meta.max_downloads} downloads used
                </div>
              </div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/35 leading-relaxed pt-2">
                This link is tied to your receipt and exclusive to ninacapone.com visitors. Please
                don&rsquo;t share — the locker tracks every download.
              </p>
              <Link
                to="/shop"
                className="inline-block text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] transition"
                data-testid="download-back-to-shop"
              >
                ← Back to Shop
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
